import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';

export type FreezeSelection = {
  fertilizationIds: string[];
  liveEmbryoIds: string[];
  tempEmbryoIds: string[];
};

const STORAGE_KEY = 'freeze_embryo_selection';

type SelectionState = FreezeSelection;

type SelectionAction =
  | { type: 'SET_FERT_IDS'; ids: string[] }
  | { type: 'SET_LIVE_IDS'; ids: string[] }
  | { type: 'SET_TEMP_IDS'; ids: string[] }
  | { type: 'SET_ALL'; selection: FreezeSelection }
  | { type: 'CLEAR' };

function selectionReducer(
  state: SelectionState,
  action: SelectionAction,
): SelectionState {
  switch (action.type) {
    case 'SET_FERT_IDS':
      return { ...state, fertilizationIds: action.ids };
    case 'SET_LIVE_IDS':
      return { ...state, liveEmbryoIds: action.ids };
    case 'SET_TEMP_IDS':
      return { ...state, tempEmbryoIds: action.ids };
    case 'SET_ALL':
      return {
        ...state,
        fertilizationIds: action.selection.fertilizationIds,
        liveEmbryoIds: action.selection.liveEmbryoIds,
        tempEmbryoIds: action.selection.tempEmbryoIds,
      };
    case 'CLEAR':
      return { ...initialState };
    default:
      return state;
  }
}

const initialState: SelectionState = {
  fertilizationIds: [],
  liveEmbryoIds: [],
  tempEmbryoIds: [],
};

/** Rehydrate selection from localStorage so it survives a page reload (parity with v1). */
function loadPersistedState(): SelectionState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return initialState;
    const parsed = JSON.parse(raw) as Partial<FreezeSelection>;
    return {
      ...initialState,
      fertilizationIds: parsed.fertilizationIds ?? [],
      liveEmbryoIds: parsed.liveEmbryoIds ?? [],
      tempEmbryoIds: parsed.tempEmbryoIds ?? [],
    };
  } catch {
    return initialState;
  }
}

type FreezeSelectionContextValue = SelectionState & {
  dispatch: React.Dispatch<SelectionAction>;
  totalSelected: number;
};

const FreezeSelectionContext =
  createContext<FreezeSelectionContextValue | null>(null);

export function FreezeSelectionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, dispatch] = useReducer(
    selectionReducer,
    undefined,
    loadPersistedState,
  );

  // Persist selection so it survives reloads; CLEAR writes empty arrays (effectively clears it).
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          fertilizationIds: state.fertilizationIds,
          liveEmbryoIds: state.liveEmbryoIds,
          tempEmbryoIds: state.tempEmbryoIds,
        }),
      );
    } catch {
      // ignore storage failures (private mode, quota, etc.)
    }
  }, [state.fertilizationIds, state.liveEmbryoIds, state.tempEmbryoIds]);

  const totalSelected = state.liveEmbryoIds.length + state.tempEmbryoIds.length;

  const value = useMemo(
    () => ({ ...state, dispatch, totalSelected }),
    [state, dispatch, totalSelected],
  );

  return (
    <FreezeSelectionContext.Provider value={value}>
      {children}
    </FreezeSelectionContext.Provider>
  );
}

export function useFreezeSelection() {
  const ctx = useContext(FreezeSelectionContext);
  if (!ctx) {
    throw new Error(
      'useFreezeSelection must be used within FreezeSelectionProvider',
    );
  }
  return ctx;
}

export function useClearSelection() {
  const { dispatch } = useFreezeSelection();
  return useCallback(() => dispatch({ type: 'CLEAR' }), [dispatch]);
}
